import React, { useState, useEffect } from "react";
import { useParams, NavLink } from "react-router-dom";
import {
  FiBox,
  FiCalendar,
  FiCheck,
  FiChevronRight,
  FiCopy,
  FiEye,
  FiHome,
  FiMail,
  FiPhone,
  FiPlus,
  FiUser,
} from "react-icons/fi";
import { MdFemale, MdMale, MdOutlineInventory2, MdPerson } from "react-icons/md";
import { getUserById, getBookingsUserById } from "../Services/userService";
import { RoutesPaths } from "../Router/constant";
import Skeleton from '../Components/SkeletonLoader'

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

const formatStatus = (status = "") =>
  String(status).replace(/\b\w/g, (char) => char.toUpperCase());

export default function Usermanagementdetail() {
  const { id } = useParams();
  const [singleUserData, setSingleUserData] = useState(null);
  const [bookingUserData, setBookingUserData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (id) {
      fetchUserData();
      fetchBookingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const isUserLoading = singleUserData === null;

  const fetchUserData = async () => {
    try {
      const usersResp = await getUserById(id);
      const user = usersResp?.data?.data?.user;
      setSingleUserData(user);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const fetchBookingData = async () => {
    try {
      const bookingResp = await getBookingsUserById(id);
      const booking = bookingResp?.data;
      setBookingUserData(booking);
    } catch (error) {
      console.error("Error fetching booking:", error);
    }
  };

  const copyUserId = async () => {
    try {
      await navigator.clipboard.writeText(singleUserData?._id || id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const orders = bookingUserData?.data?.orders || [];
  const totalProducts = orders.flatMap((order) => order.productDetails || []).length;
  const createOrderPath = `${RoutesPaths.ADDORDER}/${id}`;

  const phone = singleUserData?.mobileNumber
    ? `${singleUserData?.countryCode || ""} ${singleUserData.mobileNumber}`.trim()
    : "--";

  const joinedAt = singleUserData?.createdAt
    ? new Date(singleUserData.createdAt).toLocaleString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    : "--";

  const dob = singleUserData?.dob
    ? new Date(singleUserData.dob).toLocaleString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    })
    : "--";

  const gender = singleUserData?.gender || "--";
  const GenderIcon = /^f/i.test(gender) ? MdFemale : /^m/i.test(gender) ? MdMale : FiUser;

  // Only show an account-state pill when the API actually sends the flag.
  const isActive = singleUserData?.isActive;

  const infoItems = [
    { label: "Email", value: singleUserData?.email || "--", Icon: FiMail, width: 200 },
    { label: "Phone number", value: phone, Icon: FiPhone, width: 120 },
    { label: "Date and time", value: joinedAt, Icon: FiCalendar, width: 160 },
    { label: "DOB", value: dob, Icon: FiUser, width: 120 },
    { label: "Gender", value: gender, Icon: GenderIcon, width: 80, capitalize: true },
  ];

  return (
    <div className="container-fluid qd-page">
      {/* Breadcrumb */}
      <nav className="qd-breadcrumb">
        <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
          <FiHome />
        </NavLink>
        <FiChevronRight />
        <NavLink to={RoutesPaths.USERMANAGEMENT}>User management</NavLink>
        <FiChevronRight />
        <span className="qd-breadcrumb-current">User id : {id}</span>
      </nav>

      {/* Profile card */}
      <div className="qd-card qu-profile">
        <div className="qu-identity">
          <div className="qu-avatar">
            {isUserLoading ? "" : getInitials(singleUserData?.fullName)}
          </div>
          <div className="qu-identity-text">
            <h2 className="qu-name">
              {isUserLoading ? <Skeleton width={180} height={26} /> : singleUserData?.fullName || "--"}
            </h2>
            {!isUserLoading && (
              <div className="qu-pills">
                {typeof isActive === "boolean" && (
                  <span className={`qu-pill ${isActive ? "qu-pill-active" : "qu-pill-inactive"}`}>
                    <span className="qu-dot" />
                    {isActive ? "Active" : "Inactive"}
                  </span>
                )}
                {typeof singleUserData?.isVerified === "boolean" && (
                  <span className={`qd-pill ${singleUserData.isVerified ? "qd-pill-success" : "qd-pill-warning"}`}>
                    {singleUserData.isVerified ? "Verified" : "Un-verified"}
                  </span>
                )}
              </div>
            )}
            <div className="qu-userid">
              <span>User ID: {singleUserData?._id || id}</span>
              <button
                type="button"
                className="qu-copy"
                onClick={copyUserId}
                title={copied ? "Copied" : "Copy user ID"}
                aria-label="Copy user ID"
              >
                {copied ? <FiCheck /> : <FiCopy />}
              </button>
            </div>
          </div>
        </div>

        <div className="qu-info-grid">
          {infoItems.map(({ label, value, Icon, width, capitalize }) => (
            <div className="qu-info" key={label}>
              <span className="qu-info-icon">
                <Icon />
              </span>
              <div className="qu-info-text">
                <span>{label}</span>
                {isUserLoading ? (
                  <Skeleton width={width} height={18} />
                ) : (
                  <strong className={capitalize ? "text-capitalize" : ""}>{value}</strong>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="qu-art" aria-hidden="true">
          <span className="qu-art-circle" />
          <MdPerson className="qu-art-person" />
        </div>
      </div>

      {/* Orders */}
      <div className="qd-card qd-panel">
        <div className="qd-panel-header">
          <div className="qd-panel-title">
            <span className="qd-panel-icon">
              <FiBox />
            </span>
            <div>
              <h6>Order details</h6>
              <p className="qd-panel-sub">All orders created by this user are listed below.</p>
            </div>
          </div>
          <div className="qd-bookings-tools">
            {totalProducts > 5 && (
              <NavLink
                to={`${RoutesPaths.BOOKINGMANAGEMENTDETAIL}/${singleUserData?._id}`}
                className="qd-btn qd-btn-outline"
              >
                View more
              </NavLink>
            )}
            <NavLink to={createOrderPath} className="qd-btn qd-btn-primary">
              <FiPlus /> Create order
            </NavLink>
          </div>
        </div>

        <div className="qd-table-wrap">
          <table className="qd-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer name</th>
                <th>Phone number</th>
                <th>Currency</th>
                <th>Date</th>
                <th>Status</th>
                <th>Total products</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!bookingUserData ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i}>
                    <td><Skeleton width={80} /></td>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={60} /></td>
                    <td><Skeleton width={90} /></td>
                    <td><Skeleton width={100} height={24} /></td>
                    <td><Skeleton width={40} /></td>
                    <td><Skeleton width={70} /></td>
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="qu-empty-cell">
                    <div className="qu-empty">
                      <span className="qu-empty-art">
                        <MdOutlineInventory2 />
                      </span>
                      <h6>No order found</h6>
                      <p>This user hasn&apos;t created any orders yet.</p>
                      <NavLink to={createOrderPath} className="qd-btn qd-btn-primary">
                        <FiPlus /> Create order
                      </NavLink>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.slice(0, 5).map((order, index) => (
                  <tr key={`${order._id}-${index}`}>
                    <td className="qd-mono">#{order.orderId}</td>
                    <td><strong className="qd-user-name">{order.name || "N/A"}</strong></td>
                    <td>{order.phoneNumber || "N/A"}</td>
                    <td>{order.currency?.toUpperCase() || "N/A"}</td>
                    <td>
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-GB") : "N/A"}
                    </td>
                    <td>
                      <span
                        className={`qd-status qd-status-${String(order.status || "").toLowerCase().replace(/\s+/g, "")}`}
                      >
                        {order.status ? formatStatus(order.status) : "N/A"}
                      </span>
                    </td>
                    <td>{order.productDetails?.length || 0}</td>
                    <td>
                      <NavLink
                        to={`${RoutesPaths.BOOKINGMANAGEMENTORDERHISTORY}/${order._id}`}
                        className="qd-btn qd-btn-outline qd-btn-sm"
                      >
                        <FiEye /> View
                      </NavLink>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
